import {
  Palette,
  Layers,
  Clock,
  Activity,
  Database,
  AlertCircle,
} from "lucide-react";
import type { MapStyleLibraryTableProps } from "../../contracts/map-styles";
import { cn } from "../../lib/utils";
import { MapStyleRowActions } from "./MapStyleRowActions";
import { MapStyleDependencySummary } from "./MapStyleDependencySummary";
import { MapAssetStatList } from "../map-assets/MapAssetStatList";

export function MapStyleLibraryTable({
  styles,
  loading,
  errorMessage,
  emptyTitle = "No styles found",
  emptyDescription = "Try adjusting your search or filters.",
  onAction,
  className,
}: MapStyleLibraryTableProps) {
  if (loading) {
    return (
      <div className={cn("flex min-h-[400px] flex-col items-center justify-center space-y-4 rounded-xl border border-dashed border-border/60 bg-muted/5 py-12 text-center", className)}>
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
        <p className="text-xs font-bold text-muted-foreground animate-pulse">Loading styles...</p>
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className={cn("flex min-h-[400px] flex-col items-center justify-center space-y-4 rounded-xl border border-dashed border-destructive/20 bg-destructive/5 py-12 text-center", className)}>
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertCircle className="h-5 w-5" />
        </div>
        <div className="space-y-1">
          <h3 className="text-sm font-black text-destructive">Error Loading Styles</h3>
          <p className="max-w-xs text-xs text-destructive/80">{errorMessage}</p>
        </div>
      </div>
    );
  }

  if (styles.length === 0) {
    return (
      <div className={cn("flex min-h-[400px] flex-col items-center justify-center space-y-4 rounded-xl border border-dashed border-border/60 bg-muted/5 py-12 text-center", className)}>
        <div className="flex h-10 w-10 items-center justify-center rounded-full bg-muted text-muted-foreground">
          <Palette className="h-5 w-5" />
        </div>
        <div className="space-y-1">
          <h3 className="text-sm font-black text-foreground">{emptyTitle}</h3>
          <p className="max-w-xs text-xs text-muted-foreground/80">{emptyDescription}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={cn("space-y-3", className)}>
      {styles.map((style) => (
        <div
          key={style.id}
          className="group relative overflow-hidden rounded-lg border border-border/60 bg-card transition-all hover:border-border hover:shadow-sm"
        >
          <div className="p-4">
            <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
              {/* Identity & Core Info */}
              <div className="min-w-0 flex-1 space-y-3">
                <div className="flex flex-wrap items-start gap-2.5">
                  <h3 className="min-w-0 flex-1 break-all text-sm font-black tracking-tight text-foreground transition-colors group-hover:text-primary sm:truncate sm:break-normal">
                    {style.label}
                  </h3>
                  
                  <div className="flex flex-wrap items-center gap-1.5">
                    {style.shareScope && (
                      <span className={cn(
                        "rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ring-1",
                        style.shareScope === "tenant" && "bg-primary/10 text-primary ring-primary/20",
                        style.shareScope === "shared" && "bg-muted text-muted-foreground ring-border",
                        style.shareScope === "public" && "bg-success/10 text-success ring-success/20"
                      )}>
                        {style.shareScope}
                      </span>
                    )}
                    {style.statusBadges?.map((badge) => (
                      <span
                        key={badge.id}
                        className={cn(
                          "rounded px-1.5 py-0.5 text-[9px] font-black uppercase tracking-wider ring-1",
                          (!badge.tone || badge.tone === "default") && "bg-muted text-muted-foreground ring-border",
                          badge.tone === "success" && "bg-success/10 text-success ring-success/20",
                          badge.tone === "warning" && "bg-warning/10 text-warning ring-warning/20",
                          badge.tone === "danger" && "bg-destructive/10 text-destructive ring-destructive/20"
                        )}
                      >
                        {badge.label}
                      </span>
                    ))}
                  </div>
                </div>

                {style.description && (
                  <p className="line-clamp-2 text-xs text-muted-foreground/80 sm:line-clamp-1">
                    {style.description}
                  </p>
                )}

                <div className="flex flex-wrap items-center gap-x-6 gap-y-2">
                  {style.layerCountLabel && (
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground">
                      <Layers className="h-3 w-3" />
                      {style.layerCountLabel}
                    </div>
                  )}
                  {style.sourceCountLabel && (
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground">
                      <Database className="h-3 w-3" />
                      {style.sourceCountLabel}
                    </div>
                  )}
                  {style.runtimeStateLabel && (
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground">
                      <Activity className="h-3 w-3" />
                      {style.runtimeStateLabel}
                    </div>
                  )}
                  {style.updatedLabel && (
                    <div className="flex items-center gap-1.5 text-[10px] font-bold text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {style.updatedLabel}
                    </div>
                  )}
                </div>

                <div className="overflow-hidden pt-1">
                  <MapStyleDependencySummary
                    dependencies={style.dependencies}
                    compact
                    className="opacity-80 transition-opacity group-hover:opacity-100"
                  />
                </div>
              </div>

              {/* Actions Section */}
              <div className="w-full md:w-auto">
                <MapStyleRowActions
                  actions={style.actions}
                  onAction={(actionId) => onAction(style.id, actionId)}
                  className="w-full md:w-auto"
                />
              </div>
            </div>

            {style.summaryItems && style.summaryItems.length > 0 && (
              <div className="mt-4 border-t border-border/40 pt-3">
                <MapAssetStatList items={style.summaryItems} />
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
